export interface FxUniforms {
  brightness?: number; // 0-2 (1 = normal)
  contrast?: number;   // 0-2 (1 = normal)
  saturation?: number; // 0-2 (1 = normal)
}

/** Initialize a WebGL2 context from a canvas. Returns null if unsupported. */
export function initWebGLFx(canvas: HTMLCanvasElement): WebGL2RenderingContext | null {
  try {
    const gl = canvas.getContext('webgl2');
    return gl ?? null;
  } catch {
    return null;
  }
}

const VERT_SRC = `#version 300 es
in vec2 a_position;
out vec2 v_texCoord;
void main() {
  v_texCoord = a_position * 0.5 + 0.5;
  v_texCoord.y = 1.0 - v_texCoord.y;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAG_SRC = `#version 300 es
precision mediump float;
in vec2 v_texCoord;
uniform sampler2D u_texture;
uniform float u_brightness;
uniform float u_contrast;
uniform float u_saturation;
out vec4 outColor;
void main() {
  vec4 texColor = texture(u_texture, v_texCoord);
  vec3 color = texColor.rgb;

  // Brightness
  color = color * u_brightness;

  // Contrast: (color - 0.5) * contrast + 0.5
  color = (color - 0.5) * u_contrast + 0.5;

  // Saturation: mix(grey, color, saturation)
  float grey = dot(color, vec3(0.299, 0.587, 0.114));
  color = mix(vec3(grey), color, u_saturation);

  outColor = vec4(clamp(color, 0.0, 1.0), texColor.a);
}
`;

function compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

/* ── Cached GPU resources — compile once, reuse every frame ────────── */
let _cachedGl: WebGL2RenderingContext | null = null;
let _cachedProgram: WebGLProgram | null = null;
let _cachedVao: WebGLVertexArrayObject | null = null;
let _cachedVbo: WebGLBuffer | null = null;
let _cachedTexture: WebGLTexture | null = null;
let _uTexture: WebGLUniformLocation | null = null;
let _uBrightness: WebGLUniformLocation | null = null;
let _uContrast: WebGLUniformLocation | null = null;
let _uSaturation: WebGLUniformLocation | null = null;

/** Delete all cached GL resources and null the references. */
function _disposeCache(): void {
  if (_cachedGl) {
    if (_cachedTexture) _cachedGl.deleteTexture(_cachedTexture);
    if (_cachedVbo) _cachedGl.deleteBuffer(_cachedVbo);
    if (_cachedVao) _cachedGl.deleteVertexArray(_cachedVao);
    if (_cachedProgram) _cachedGl.deleteProgram(_cachedProgram);
  }
  _cachedGl = null;
  _cachedProgram = null;
  _cachedVao = null;
  _cachedVbo = null;
  _cachedTexture = null;
  _uTexture = null;
  _uBrightness = null;
  _uContrast = null;
  _uSaturation = null;
}

/** Public cleanup — call before losing the WebGL context. */
export function disposeFxCache(): void {
  _disposeCache();
}

/**
 * Ensure all GPU resources are compiled / allocated for the given context.
 * Returns true if resources are ready, false on failure.
 */
function _ensureResources(gl: WebGL2RenderingContext): boolean {
  // Fast path: everything cached for this context
  if (
    _cachedGl === gl &&
    !gl.isContextLost() &&
    _cachedProgram &&
    _cachedVao &&
    _cachedVbo &&
    _cachedTexture
  ) {
    return true;
  }

  // Context changed or first call — dispose stale cache
  _disposeCache();
  _cachedGl = gl;

  // Compile shaders
  const vert = compileShader(gl, gl.VERTEX_SHADER, VERT_SRC);
  const frag = compileShader(gl, gl.FRAGMENT_SHADER, FRAG_SRC);
  if (!vert || !frag) {
    if (vert) gl.deleteShader(vert);
    if (frag) gl.deleteShader(frag);
    _disposeCache();
    return false;
  }

  // Link program
  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vert);
    gl.deleteShader(frag);
    _disposeCache();
    return false;
  }
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteShader(vert);
    gl.deleteShader(frag);
    gl.deleteProgram(program);
    _disposeCache();
    return false;
  }

  // Shaders are baked into the program — detach + delete to save VRAM
  gl.detachShader(program, vert);
  gl.detachShader(program, frag);
  gl.deleteShader(vert);
  gl.deleteShader(frag);

  _cachedProgram = program;

  // Cache uniform locations
  _uTexture = gl.getUniformLocation(program, 'u_texture');
  _uBrightness = gl.getUniformLocation(program, 'u_brightness');
  _uContrast = gl.getUniformLocation(program, 'u_contrast');
  _uSaturation = gl.getUniformLocation(program, 'u_saturation');

  // Fullscreen quad VAO + VBO (STATIC_DRAW — uploaded once)
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) {
    if (vao) gl.deleteVertexArray(vao);
    if (vbo) gl.deleteBuffer(vbo);
    _disposeCache();
    return false;
  }

  const positions = new Float32Array([
    -1, -1,
     1, -1,
    -1,  1,
    -1,  1,
     1, -1,
     1,  1,
  ]);

  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

  const posLoc = gl.getAttribLocation(program, 'a_position');
  gl.enableVertexAttribArray(posLoc);
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  _cachedVao = vao;
  _cachedVbo = vbo;

  // Reusable texture with LINEAR filter + CLAMP_TO_EDGE
  const texture = gl.createTexture();
  if (!texture) {
    _disposeCache();
    return false;
  }
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);

  _cachedTexture = texture;

  return true;
}

/**
 * Apply FX uniforms to a source ImageBitmap and render to the WebGL canvas.
 * Returns true if rendering succeeded, false if WebGL failed (caller should hide the overlay).
 */
export function applyFxToCanvas(
  gl: WebGL2RenderingContext,
  source: ImageBitmap,
  uniforms: FxUniforms
): boolean {
  // Ensure GPU resources are compiled and ready
  if (!_ensureResources(gl)) return false;

  const brightness = uniforms.brightness ?? 1;
  const contrast = uniforms.contrast ?? 1;
  const saturation = uniforms.saturation ?? 1;

  // Resize viewport FIRST (canvas resize can reset GL state)
  const canvas = gl.canvas as HTMLCanvasElement;
  if (canvas.width !== source.width || canvas.height !== source.height) {
    canvas.width = source.width;
    canvas.height = source.height;
  }
  gl.viewport(0, 0, source.width, source.height);

  // Upload source frame to cached texture
  gl.bindTexture(gl.TEXTURE_2D, _cachedTexture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

  // Draw with cached program + VAO
  gl.useProgram(_cachedProgram);
  gl.bindVertexArray(_cachedVao);

  gl.uniform1i(_uTexture, 0);
  gl.uniform1f(_uBrightness, brightness);
  gl.uniform1f(_uContrast, contrast);
  gl.uniform1f(_uSaturation, saturation);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, _cachedTexture);

  gl.drawArrays(gl.TRIANGLES, 0, 6);

  // Unbind — but do NOT delete cached resources
  gl.bindVertexArray(null);
  gl.bindTexture(gl.TEXTURE_2D, null);

  return true;
}
